import { Badge } from '@/components/ui/badge'
import { Card, CardTitle } from '@/components/ui/card'
import type { MemberPlotDetail, PlotDetail } from '@riverpark/types'

type PlotFields = MemberPlotDetail | PlotDetail

function plotLocationLine(plot: PlotFields): string {
  if (plot.plotType === 'special_use') return 'Special-use area'
  const parts = [plot.courtOrStreet, plot.section].filter(Boolean)
  return parts.join(' · ') || '—'
}

export function PlotDetailsReadonly({ plot }: { plot: PlotFields }) {
  const statusVariant =
    plot.status === 'occupied' ? 'paid' : plot.status === 'reserved' ? 'pending' : 'draft'

  return (
    <Card className="p-6">
      <CardTitle>Plot details</CardTitle>
      <dl className="mt-4 space-y-3 font-sans text-sm">
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">Plot number</dt>
          <dd className="font-medium text-ink">{plot.newNumber}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">Legacy number</dt>
          <dd className="text-ink">{plot.oldNumber}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">Status</dt>
          <dd>
            <Badge variant={statusVariant} className="capitalize">
              {plot.status}
            </Badge>
          </dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">Type</dt>
          <dd className="capitalize text-ink">{plot.plotType.replace('_', ' ')}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">Development status</dt>
          <dd>
            <Badge variant={plot.developmentStatus === 'developer' ? 'paid' : 'draft'}>
              {plot.developmentStatus === 'developer' ? 'Developer' : 'Owner'}
            </Badge>
          </dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">Location</dt>
          <dd className="text-right text-ink">{plotLocationLine(plot)}</dd>
        </div>
      </dl>

      <dl className="mt-6 space-y-3 border-t border-border-2 pt-4 font-sans text-sm">
        <div>
          <dt className="text-muted-foreground">Borehole meter no.</dt>
          <dd className="text-ink">{plot.boreholeMeterNumber ?? '—'}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">KPLC meter no.</dt>
          <dd className="text-ink">{plot.kplcMeterNumber ?? '—'}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Section</dt>
          <dd className="text-ink">{plot.section ?? '—'}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Main avenue</dt>
          <dd className="text-ink">{plot.mainAvenue ?? '—'}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Court / street</dt>
          <dd className="text-ink">{plot.courtOrStreet ?? '—'}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Notes</dt>
          <dd className="text-ink">{plot.notes ?? '—'}</dd>
        </div>
      </dl>
    </Card>
  )
}

export { plotLocationLine }
